import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Routes, Route } from 'react-router-dom'
import { Box, Container, CircularProgress } from '@mui/material'
import { onAuthStateChanged } from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'

import MainAppHeader from './components/navigation/MainAppHeader'
import MobileBottomNavigationMenu from './components/navigation/MobileBottomNavigationMenu'
import NotificationBanner from './components/NotificationBanner'
import ConfirmationDialog from './components/ConfirmationDialog'
import PullToRefresh from './components/PullToRefresh'

// Pages
import LandingPage from './pages/LandingPage'
import FlockManagerPage from './pages/FlockManagerPage'
import EntryDetailsPage from './pages/EntryDetailsPage'

import { handleLogin } from './store/slices/userSlice'
import AddSingleEntryPage from './pages/AddSingleEntryPage'
import { auth, db } from './plugins/firebase'

function App() {
  const dispatch = useDispatch()
  const [authChecking, setAuthChecking] = useState(true)

  useEffect(() => {
    // Listen for auth state changes and restore session on page load
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // User is signed in, fetch their profile from Firestore
          const userRef = doc(db, 'users', user.uid)
          const userDoc = await getDoc(userRef)

          if (userDoc.exists()) {
            // Restore user session in Redux
            dispatch({
              type: 'user/handleLogin/fulfilled',
              payload: userDoc.data()
            })
          }
        } catch (error) {
          console.error('Error restoring user session:', error)
        }
      }
      setAuthChecking(false)
    })

    // Cleanup subscription on unmount
    return () => unsubscribe()
  }, [dispatch])

  // Show loading spinner while checking auth state
  if (authChecking) {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh'
      }}>
        <CircularProgress />
      </Box>
    )
  }

  const handleRefresh = async () => {
    // Reset app state and reinitialize
    window.location.reload()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <MainAppHeader />
      
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1,
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: { xs: '80px', md: 0 }, // Add bottom padding on mobile for navigation
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        <PullToRefresh onRefresh={handleRefresh}>
          <Container 
            maxWidth="xl" 
            sx={{ 
              py: { xs: 1, sm: 2 },
              px: { xs: 1, sm: 3 }
            }}
          >
            <NotificationBanner />
            <ConfirmationDialog />
            
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/flock-manager" element={<FlockManagerPage />} />
              <Route path="/add-entry" element={<AddSingleEntryPage />} />
              <Route path="/entry/:id" element={<EntryDetailsPage />} />
            </Routes>
          </Container>
        </PullToRefresh>
      </Box>
      
      <MobileBottomNavigationMenu />
    </Box>
  )
}

export default App